// The dependants-turned-18 worklist (UC-PATIENTS-011; PAT-07, PAT-44, BR-07) at /patients/turned-18. The prototype has no page
// of its own — Today's "Dependant turned 18 this month" line and the patients list (design/screens/today.html,
// patients.html) give its vocabulary: the name cell with the account code, DOB · age, the main account's /0 code, the scheme
// with "own scheme", the status chip — and no relationship column, since only a Child dependant is ever listed
// (BR-PATIENTS-020). Each one waits here, oldest birthday first, until staff detach them (UC-PATIENTS-009/010) or keep them
// on the account (PATCH turnedEighteenReviewed); nothing happens by itself.
// Branch and "Show kept" live in the URL. The medical-aid hint comes from one search per main account code — a search writes
// no access entry, where reading every family would log a profile read the user never made (BR-PATIENTS-017).
import type { DependantTurnedEighteenEntry } from "@visionx/contracts";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { patients } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Avatar, Checkbox, Empty, ErrorAlert, Loading, Pager, Panel, Select } from "../ui/bits";
import { NoAccessPanel, PageHead } from "./common";
import { DetachModal, setTurnedEighteenReviewed } from "./PatientsDetach";
import { PatientStatusChip, dobAge, patientInitials, recordPath, useBranchNames, writeTitle } from "./PatientsShared";

const PAGE = 25;
const dash = <span className="faint">—</span>;
/** The day the dependant turned 18, from the date of birth (29 Feb rolls to 1 Mar). */
export function turned18On(dateOfBirth: string): string {
  const [y, m, d] = dateOfBirth.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y + 18, m - 1, d)).toISOString().slice(0, 10);
}
/** `MOK001/0` → `MOK001`: the prefix every member of the family's codes starts with, which the search matches. */
const familyCode = (accountCode: string): string => accountCode.replace(/\/\d+$/, "");

export function PatientsTurned18() {
  const sess = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const { name: branchName, all: allBranches } = useBranchNames();
  const [sp, setSp] = useSearchParams();
  const branchId = sp.get("branch") ?? undefined;
  const kept = sp.get("kept") === "true";
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const list = useQuery({
    queryKey: ["patients", "turned-18", branchId ?? "", kept, cursor ?? ""],
    queryFn: () => patients.turnedEighteen({ branchId, includeReviewed: kept ? "true" : undefined }, { limit: PAGE, cursor }),
  });
  const rows = list.data?.data.items ?? [];
  const families = [...new Set(rows.map((r) => familyCode(r.mainAccount.accountCode)).filter((c) => c.length >= 2))];
  const hints = useQueries({
    queries: families.map((code) => ({ queryKey: ["patients", "search", code, "turned-18"], queryFn: () => patients.search(code, { limit: 50 }), staleTime: 60_000 })),
  });
  const [detaching, setDetaching] = useState<DependantTurnedEighteenEntry | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const canManage = can(sess, "patients.family.manage");
  const canWrite = can(sess, "patients.patient.write");

  const setParam = (k: string, v: string | null) => { setCursors([]); setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true }); };
  const refresh = (r: DependantTurnedEighteenEntry) => {
    void qc.invalidateQueries({ queryKey: ["patients"] });
    void qc.invalidateQueries({ queryKey: ["patient", r.id] });
    void qc.invalidateQueries({ queryKey: ["patient", r.mainAccount.id] });
  };
  const review = async (r: DependantTurnedEighteenEntry, reviewed: boolean) => {
    setBusyId(r.id);
    setNotice(null);
    try {
      await setTurnedEighteenReviewed(r.id, reviewed);
      refresh(r);
      setNotice(reviewed
        ? <Alert tone="ok" actions={canWrite && <button className="btn sm" type="button" onClick={() => void review(r, false)}>Undo</button>}><b>{r.displayName} kept on {familyCode(r.mainAccount.accountCode)}.</b> Off the worklist; the main account's turned-18 alert is cleared. The review is recorded with your name.</Alert>
        : <Alert tone="ok"><b>{r.displayName} is back on the worklist.</b></Alert>);
    } catch (err) {
      setNotice(<Alert tone="crit"><b>{reviewed ? "Not kept." : "Not put back."}</b> {describeError(err)}</Alert>);
    } finally { setBusyId(null); }
  };
  const closeDetach = useCallback(() => setDetaching(null), []);

  const hint = (r: DependantTurnedEighteenEntry): ReactNode => {
    const i = families.indexOf(familyCode(r.mainAccount.accountCode));
    const h = i >= 0 ? hints[i] : undefined;
    if (!h || h.isError) return dash;
    if (h.isPending) return <span className="faint">…</span>;
    const found = h.data.data.items;
    const me = found.find((x) => x.id === r.id);
    const main = found.find((x) => x.id === r.mainAccount.id);
    if (!me) return dash;
    if (!me.schemeName) return <span className="faint">No medical aid</span>;
    if (main && main.schemeName === me.schemeName) return <>{me.schemeName}<div className="small faint">same scheme as the main member</div></>;
    return <>{me.schemeName}<div className="small faint">own scheme · consider detaching</div></>;
  };

  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  const first = cursors.length * PAGE + 1;
  return (
    <section className="screen" id="s-patients">
      <div className="content">
        <PageHead title="Dependants turned 18" sub="Child dependants aged 18 or over, oldest birthday first. Nothing is detached automatically — detach each one, or keep them on the account." />
        <div className="toolbar">
          <Select aria-label="Branch" boxClass="w-sel" value={branchId ?? ""} onChange={(e) => setParam("branch", e.target.value || null)}>
            <option value="">Branch · All branches</option>
            {allBranches.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
          </Select>
          <Checkbox checked={kept} onChange={(v) => setParam("kept", v ? "true" : null)}>Show kept</Checkbox>
          <span className="help">{kept ? "Dependants kept on their account are shown with who kept them" : "Dependants kept on their account are hidden"}</span>
          <Link className="btn sm right" to="/patients">All patients</Link>
        </div>
        {notice}
        {list.isPending && <Loading what="the worklist" />}
        {list.isError && (denied ? <NoAccessPanel page="the turned-18 worklist" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel title={kept ? "Dependants turned 18, kept ones included" : "Waiting for a decision"} meta={rows.length === 0 ? "" : `${first}–${first + rows.length - 1}${list.data.data.nextCursor ? " · more" : ""} · by birthday`} tight
            foot={<>
              <span className="small faint">Detaching keeps every record and makes the old account code a searchable alias</span>
              <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data!.data.nextCursor!])} />
            </>}>
            {rows.length === 0 ? (
              <Empty title="No dependant is waiting for a decision.">
                Child dependants appear here from their 18th birthday until they are detached or kept on the account.{kept ? "" : " Kept dependants are hidden; tick Show kept to see them."}
              </Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Dependant</th><th>DOB / age</th><th>Turned 18</th><th>Main account</th><th>Home branch</th><th>Medical aid</th><th>Status</th><th className="r"></th></tr></thead>
                  <tbody>
                    {rows.map((r) => {
                      const busy = busyId === r.id;
                      return (
                        <tr key={r.id}>
                          <td><div className="name-cell"><Avatar initials={patientInitials(r)} size="sm" /><div><Link className="nw" to={recordPath(r.id)}>{r.displayName}</Link><div className="sub mono">{r.accountCode}</div></div></div></td>
                          <td className="nw">{dobAge(r.dateOfBirth, r.age)}</td>
                          <td className="nw">{formatDate(turned18On(r.dateOfBirth))}</td>
                          <td>{r.mainAccount.accountCode ? <Link className="mono" to={recordPath(r.mainAccount.id, "family")}>{r.mainAccount.accountCode}</Link> : dash}<div className="small faint">{r.mainAccount.displayName}</div></td>
                          <td>{branchName(r.homeBranchId) ?? dash}</td>
                          <td>{hint(r)}</td>
                          <td><PatientStatusChip status={r.status} />{r.reviewedAt && <div className="small faint">kept {formatDate(r.reviewedAt)}{r.reviewedBy ? ` · ${r.reviewedBy.displayName}` : ""}</div>}</td>
                          <td className="r">
                            <div className="row c gap-8" style={{ justifyContent: "flex-end" }}>
                              {!r.reviewedAt && canManage && <button className="btn sm" type="button" disabled={!online || busy} title={writeTitle(block)} onClick={() => setDetaching(r)}>Detach…</button>}
                              {!r.reviewedAt && canWrite && <button className="btn sm" type="button" disabled={!online || busy} title={writeTitle(block)} onClick={() => void review(r, true)}>{busy ? "Keeping…" : "Keep on the account"}</button>}
                              {r.reviewedAt && canWrite && <button className="btn sm" type="button" disabled={!online || busy} title={writeTitle(block)} onClick={() => void review(r, false)}>Put back on the list</button>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {detaching && <DetachModal dep={{ ...detaching, turnedEighteen: true }} mainId={detaching.mainAccount.id} onClose={closeDetach} onDone={(res) => {
          const r = detaching;
          setDetaching(null);
          refresh(r);
          setNotice(<Alert tone="ok"><b>{r.displayName} detached.</b> New account code <span className="mono">{res.newAccountCode}</span> (was <span className="mono">{res.previousAccountCode}</span>). <Link to={recordPath(res.patientId)}>Open the record</Link>.</Alert>);
        }} />}
      </div>
    </section>
  );
}
